import React, { useState, useEffect, useContext } from 'react';
import {
    Autocomplete,
    Dialog,
    Button,
    TextField,
    FormControl,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Box,
    CircularProgress,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { designTokens } from '../../../../theme';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { mergeCanvasWithIllustration } from './mergeCanvasWithIllustration';
import { useSnackbar } from '../../SnackbarProvider';
import { handleCreateOrUpdateWhiteboard } from './WhiteboardService';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { getDocumentTitle } from '../../../../components/helper/contentOperation';
import DownloadUtils from '../../../../utils/download-utils';
import {
    formatPatientAutocompleteLabel,
    formatPatientAutocompleteSearchText,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../../UIControls/patient-autocomplete-option';
import { isDuplicateClinicPatientId } from '../../../helper/patientUtils';
import { CLIENT_NAME_LABEL, PATIENT_NAME_LABEL } from '../../../helper/patientLabels';
import {
    isAuthExpiredDialogVisible,
    isAuthExpiredError,
    subscribeAuthExpired,
} from '../../../../utils/auth-error-utils';

const SaveWhiteboardModal = ({
    open,
    onClose,
    canvasRef,
    handoutData,
    onSaved,
    hasUnsavedChanges,
    showLabels,
    lastUsedColours,
    sourceCustomisedHandout = null,
}) => {
    const location = 'en-UK'; // TODO that will be clearyfy later
    const { userDataStore, authStore, whiteboardStore, imageStore, customisedHandoutStore } = useContext(StoreContext);
    const [whiteboards, setWhiteboards] = useState([]);
    const [patientList, setPatientList] = useState([]);
    const [patientListLoading, setPatientListLoading] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [clinicianData, setClinicianData] = useState({});
    const [patientType, setPatientType] = useState('existing');
    const [selectedPatient, setSelectedPatient] = useState(''); // For existing patient ID
    const [manualPatientName, setManualPatientName] = useState(''); // For new patient entry
    const [patientSurname, setPatientSurname] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [notes, setNotes] = useState('');
    const [includeAdditionalInfo, setIncludeAdditionalInfo] = useState(false);

    const showSnackbar = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [saveToMyLibrary, setSaveToMyLibrary] = useState(true); // default to My Library

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    const accountType = userDataStore?.subscriptionData?.accountType;
    const sourcePatientId = sourceCustomisedHandout?.patientId || null;
    const isPatientSavedContext = Boolean(sourceCustomisedHandout?.customisedId && sourcePatientId);
    const isMyHandoutsContext = Boolean(sourceCustomisedHandout?.customisedId && !sourcePatientId);

    // is it diagram type
    const isDiagramHandout = handoutData?.contentData?.[location]?.some((section) => section.sectionName === 'diagram');

    useEffect(() => {
        if (!isStudent && open && !saveToMyLibrary) {
            fetchPatientList();
        }
    }, [isStudent, open, saveToMyLibrary]);

    useEffect(() => {
        if (open) {
            setSaveToMyLibrary(isPatientSavedContext || (!hasUnsavedChanges && isMyHandoutsContext) ? false : true);
            setNotes('');
            setPatientType('existing');
            setSelectedPatient('');
            setManualPatientName('');
            setPatientSurname('');
            setPatientEmail('');
            setClinicPatientId('');
            setIncludeAdditionalInfo(false);
        }
    }, [open, hasUnsavedChanges, isMyHandoutsContext, isPatientSavedContext]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            setSaving(false);
            onClose();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            setSaving(false);
            onClose();
        });
    }, [open, onClose]);

    useEffect(() => {
        if (['Student', 'Professional'].includes(accountType)) {
            setSaveToMyLibrary(true);
        }
    }, [accountType]);

    const fetchClinicianData = async () => {
        try {
            console.warn('Fetching Clinician data...');
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            setClinicianData(clinicianDataResponse);
            return clinicianDataResponse;
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
            return null;
        }
    };

    const fetchPatientList = async () => {
        setPatientListLoading(true);
        try {
            const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                filterType: 'patientStatus',
                filterValue: 'active',
            });

            if (response?.patients) {
                // console.warn('Before setting state, patientList:', patientList); // Log current state
                setPatientList(response.patients);
                // console.warn('After setting state, patientList:', response.patients); // Log new state
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        } finally {
            setPatientListLoading(false);
        }
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);

        if (!isStudent && !saveToMyLibrary) {
            if (patientType === 'existing' && !selectedPatient) {
                showSnackbar('Please select an existing patient.', 'error');
                setSaving(false);
                return;
            }
            if (patientType === 'new') {
                if (!manualPatientName.trim()) {
                    showSnackbar(`${PATIENT_NAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }

                if (!patientSurname.trim()) {
                    showSnackbar(`${CLIENT_NAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }

                if (patientEmail.trim()) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(patientEmail.trim())) {
                        showSnackbar('Please provide a valid email address.', 'error');
                        setSaving(false);
                        return;
                    }
                }

                if (clinicPatientId?.trim()) {
                    const isDuplicate = await isDuplicateClinicPatientId({
                        bearerToken: authStore.idToken,
                        clinicPatientId,
                        currentPatientId: patientType === 'existing' ? selectedPatient : null,
                    });

                    if (isDuplicate) {
                        showSnackbar(
                            'Provided PMS ID exists already. Please provide different PMS ID or choose existing patient.',
                            'error',
                        );
                        setSaving(false);
                        return;
                    }
                }
            }
        }

        if (!hasUnsavedChanges && isMyHandoutsContext && saveToMyLibrary) {
            showSnackbar('This customised handout is already saved to My handouts.', 'warning');
            setSaving(false);
            return;
        }

        if (
            !hasUnsavedChanges &&
            isPatientSavedContext &&
            patientType === 'existing' &&
            selectedPatient === sourcePatientId
        ) {
            showSnackbar('Please select a different patient.', 'warning');
            setSaving(false);
            return;
        }

        if (!canvasRef.current) {
            showSnackbar('Canvas is not available. Please try again.', 'error');
            setSaving(false);
            return;
        }

        if (!isStudent) {
            console.warn('Fetching patient list before saving...');
            await fetchPatientList(); // Ensures we have the latest patients before proceeding
        }

        console.warn('Fetching clinician data before saving...');
        const clinicianData = await fetchClinicianData();

        if (!clinicianData || !clinicianData.userId) {
            console.error('Error: clinicianData.userId is undefined.');
            if (!isAuthExpiredDialogVisible()) {
                showSnackbar('Failed to retrieve user data. Please try again.', 'error');
            }
            return;
        }

        let finalPatientName;
        if (patientType === 'existing') {
            const existing = patientList.find((p) => p.patientId === selectedPatient);
            finalPatientName = existing ? existing.patientName : '';
        } else {
            finalPatientName = manualPatientName.trim();
        }

        let finalPatientSurname;
        if (patientType === 'existing') {
            const existing = patientList.find((p) => p.patientId === selectedPatient);
            finalPatientSurname = existing ? existing.patientSurname : '';
        } else {
            finalPatientSurname = patientSurname.trim();
        }

        // Merge Whiteboard + Main Illustration
        let mergedBlob;
        try {
            mergedBlob = await mergeCanvasWithIllustration(canvasRef);
        } catch (err) {
            console.error('Failed to merge images:', err);
            showSnackbar('Could not merge the whiteboard with illustration. Please try again.', 'error');
            return;
        }

        // Example: attach width/height to the returned JSON
        const rawJson = canvasRef.current.toJSON(); // Normal Fabric JSON object
        rawJson.canvasWidth = canvasRef.current.getWidth();
        rawJson.canvasHeight = canvasRef.current.getHeight();

        // Convert fabric canvas to JSON
        const canvasJson = JSON.stringify(rawJson);

        console.warn('Length of the canvas: ', new Blob([canvasJson]).size / 1024 + ' kB');
        const date = new Date();
        // YYYYMMDD_HHMM format
        const formattedDateTime = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;

        const handoutShortName = handoutData.contentName.split(' ').slice(0, 3).join('');

        const fileName = `${formattedDateTime}_${handoutShortName}`;

        let diagramVariant = null;
        if (isDiagramHandout) {
            diagramVariant = showLabels ? 'withLabels' : 'noLabels';
        }

        const saveData = {
            // TODO posible whiteboardId could be pick up here if we want to update
            userId: clinicianData.userId, // TODO in many places in UI is false usage of userId (if multiuser, not neccessery his Id represents ClinicId). Maybe better to use that somewhere on beggining to set it in store
            patientId: patientType === 'existing' ? selectedPatient : null,
            patientName: finalPatientName,
            patientSurname: finalPatientSurname,
            patientEmail,
            clinicPatientId,
            notes,
            fileName,
            canvasData: canvasJson,
            mergedImageBlob: mergedBlob, // the created PNG
            handoutId: handoutData.contentId,
            diagramVariant,
            handoutContentName: handoutData.contentName, // added these 3 fields as consequences of current search (fuse)
            handoutAlphabeticalName: handoutData.alphabeticalName,
            handoutDocumentTitle: getDocumentTitle(handoutData.contentData, 'en-UK'),
            whiteboardStatus: 'active',
            myLibrary: saveToMyLibrary,
            updatedById: userDataStore.userId,
            updatedByFirstName: userDataStore.userData.firstName || null,
            updatedByLastName: userDataStore.userData.lastName || null,
            lastUsedColours: lastUsedColours || null,
        };

        setSaving(true);
        try {
            // Call user-api function for creating a patient,
            // generate presigned URLs, upload JSON & PNG,
            // then update the DB references

            console.warn('Saving whiteboard...');
            const whiteboardResponse = await handleCreateOrUpdateWhiteboard(authStore.idToken, saveData, showSnackbar);
            console.warn('Saved data result: ', whiteboardResponse);

            if (whiteboardResponse) {
                // local storage (that's marker)
                localStorage.setItem(
                    'lastWhiteboardState',
                    JSON.stringify({
                        whiteboardId: whiteboardResponse.whiteboardId,
                        handoutId: handoutData.contentId,
                        canvasImageLink: whiteboardResponse.canvasImageLink,
                        canvasJsonLink: whiteboardResponse.canvasJsonLink,
                        fileName,
                        eTag: whiteboardResponse.eTag || null,
                        diagramVariant,
                        updatedByFirstName: userDataStore.userData.firstName || null,
                        updatedByLastName: userDataStore.userData.lastName || null,
                        patientId: saveToMyLibrary ? null : whiteboardResponse.patientId || null,
                        patientName: saveToMyLibrary ? null : finalPatientName,
                        patientSurname: saveToMyLibrary ? null : finalPatientSurname,
                        clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                        patientEmail: saveToMyLibrary ? null : patientEmail || null,
                    }),
                );
                console.warn('Saved Whiteboard:', localStorage.getItem('lastWhiteboardState'));

                if (saveToMyLibrary && whiteboardResponse.whiteboardId) {
                    const currentWhiteboards = userDataStore.userData.whiteboards || [];

                    // Optional: enforce the 300 limit
                    if (currentWhiteboards.length >= 300) {
                        showSnackbar(
                            'You have reached the maximum number of whiteboards (300) in My Library.',
                            'warning',
                        );
                        setSaving(false);
                        return;
                    } else {
                        const updatedWhiteboards = [...currentWhiteboards, whiteboardResponse.whiteboardId];

                        // Update userDataStore locally
                        userDataStore.setUserData({
                            ...userDataStore.userData,
                            whiteboards: updatedWhiteboards,
                        });

                        // Sync with backend
                        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                        const payload = {
                            whiteboards: updatedWhiteboards,
                        };

                        try {
                            if (isMultiUser) {
                                payload.action = 'updateMultiuser';
                                payload.emailAddress = userDataStore.userData.emailAddress;
                                payload.inviteId = userDataStore.userData.inviteId;
                                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
                            } else {
                                await UserApi.updateClinicianData(authStore.idToken, payload);
                            }
                        } catch (err) {
                            console.error('Failed to sync whiteboards with backend', err);
                        }
                    }
                }

                // Ensure colours are plain arrays (avoid MobX/Proxy/observable arrays)
                const safeColours = {
                    pencil: Array.isArray(lastUsedColours?.pencil) ? [...lastUsedColours.pencil] : [],
                    spray: Array.isArray(lastUsedColours?.spray) ? [...lastUsedColours.spray] : [],
                };

                const newWhiteboard = {
                    whiteboardId: whiteboardResponse.whiteboardId,
                    canvasImageLink: whiteboardResponse.canvasImageLink,
                    canvasJsonLink: whiteboardResponse.canvasJsonLink,
                    eTag: whiteboardResponse.eTag || null,
                    fileName,
                    handoutId: handoutData.contentId,
                    diagramVariant,
                    handoutContentName: handoutData.contentName,
                    handoutAlphabeticalName: handoutData.alphabeticalName,
                    handoutDocumentTitle: getDocumentTitle(handoutData.contentData, 'en-UK'),
                    notes,
                    whiteboardStatus: 'active',
                    userId: clinicianData.userId,
                    lastUsedColours: safeColours,
                    patientId: saveToMyLibrary
                        ? null
                        : patientType === 'existing'
                          ? selectedPatient
                          : whiteboardResponse.patientId || null,
                    patientName: saveToMyLibrary ? null : finalPatientName,
                    patientSurname: saveToMyLibrary ? null : finalPatientSurname,
                    patientEmail: saveToMyLibrary ? null : patientEmail,
                    clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                    handoutIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                    lastUpdated: new Date().toISOString(),
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                };

                // Update the whiteboard store
                await whiteboardStore.setWhiteboardData([newWhiteboard]);
                setWhiteboards(Object.values(whiteboardStore.whiteboardData));

                // Save Image to Image Store
                if (whiteboardResponse.canvasImageLink) {
                    const imageExists = await imageStore.getImageFromIndexedDB(whiteboardResponse.canvasImageLink);
                    if (!imageExists) {
                        // Generate presigned URL
                        const payload = {
                            files: [{ method: 'get', key: whiteboardResponse.canvasImageLink }],
                            contentCategory: 'whiteboard',
                        };
                        const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                        const presignedUrl = resp.getUrls[0].presignedUrl;

                        // Download Image
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

                        await imageStore.saveImageToIndexedDB(
                            whiteboardResponse.canvasImageLink,
                            downloadResult.base64data,
                            whiteboardResponse.eTag || null,
                        );
                    }
                }
            }

            // Diagram-only: also create a linked CH so this save appears in My Library / My Clinic
            if (isDiagramHandout && whiteboardResponse?.whiteboardId) {
                const linkedCustomisedPatientId = saveToMyLibrary
                    ? null
                    : patientType === 'existing'
                      ? selectedPatient
                      : whiteboardResponse.patientId || null;
                // Patient-linked customised handouts are historical patient records. Saving further edits must create
                // a new linked customised handout instead of overwriting the source record.
                const shouldUpdateSourceCustomisedHandout =
                    hasUnsavedChanges &&
                    sourceCustomisedHandout?.customisedId &&
                    saveToMyLibrary &&
                    isMyHandoutsContext;

                const chPayload = {
                    customisedId: shouldUpdateSourceCustomisedHandout ? sourceCustomisedHandout.customisedId : null,
                    baseContentId: handoutData.contentId,
                    baseLastUpdated: handoutData.lastUpdated || null,
                    locale: location,
                    effectiveContentData: { [location]: JSON.parse(JSON.stringify(handoutData.contentData[location])) },
                    attachedWhiteboardId: whiteboardResponse.whiteboardId,
                    attachedWhiteboardCanvasImageLink: whiteboardResponse.canvasImageLink,
                    attachedWhiteboardETag: whiteboardResponse.eTag || null,
                    notes: notes.trim() || null,
                    customisedStatus: 'active',
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                    baseContentIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                    patientId: linkedCustomisedPatientId,
                    patientName: saveToMyLibrary ? null : finalPatientName,
                    patientSurname: saveToMyLibrary ? null : finalPatientSurname,
                    patientEmail: saveToMyLibrary ? null : patientEmail || null,
                    clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                };

                try {
                    const chResponse = await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, chPayload);

                    if (chResponse?.customisedId) {
                        const fullCH = {
                            customisedId: chResponse.customisedId,
                            userId: clinicianData.userId,
                            baseContentId: chPayload.baseContentId,
                            baseLastUpdated: chPayload.baseLastUpdated,
                            locale: chPayload.locale,
                            effectiveContentData: chPayload.effectiveContentData,
                            attachedWhiteboardId: chPayload.attachedWhiteboardId,
                            attachedWhiteboardCanvasImageLink: chPayload.attachedWhiteboardCanvasImageLink,
                            attachedWhiteboardETag: chPayload.attachedWhiteboardETag,
                            notes: chPayload.notes,
                            customisedStatus: chResponse.customisedStatus,
                            lastUpdated: Date.now(),
                            updatedBy: userDataStore.userData.emailAddress,
                            updatedById: userDataStore.userId,
                            updatedByFirstName: userDataStore.userData.firstName || null,
                            updatedByLastName: userDataStore.userData.lastName || null,
                            baseContentIdUserIdCompositeKey: chPayload.baseContentIdUserIdCompositeKey,
                            patientId: chPayload.patientId,
                            patientName: chPayload.patientName,
                            patientSurname: chPayload.patientSurname,
                            patientEmail: chPayload.patientEmail,
                            clinicPatientId: chPayload.clinicPatientId,
                        };

                        if (customisedHandoutStore) {
                            await customisedHandoutStore.addOrUpdateCustomisedHandout(fullCH);
                        }

                        if (saveToMyLibrary) {
                            const currentCHs = userDataStore.userData.customisedHandouts || [];
                            if (!currentCHs.includes(chResponse.customisedId)) {
                                const updatedCHs = [...currentCHs, chResponse.customisedId];
                                userDataStore.setUserData({
                                    ...userDataStore.userData,
                                    customisedHandouts: updatedCHs,
                                });

                                const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                                const syncPayload = { customisedHandouts: updatedCHs };
                                if (isMultiUser) {
                                    syncPayload.action = 'updateMultiuser';
                                    syncPayload.emailAddress = userDataStore.userData.emailAddress;
                                    syncPayload.inviteId = userDataStore.userData.inviteId;
                                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, syncPayload);
                                } else {
                                    await UserApi.updateClinicianData(authStore.idToken, syncPayload);
                                }
                            }
                        }
                    }
                } catch (chErr) {
                    console.error('Failed to create linked CH for diagram WB:', chErr);
                    // Non-fatal — WB already saved; CH creation failure is silent degradation
                }
            }

            // Persist recent colours to the user (or multiuser)
            try {
                const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                if (isMultiUser) {
                    const payload = {
                        action: 'updateMultiuser',
                        emailAddress: userDataStore.userData.emailAddress,
                        inviteId: userDataStore.userData.inviteId,
                        lastUsedColours,
                    };
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
                    // keep local store in sync
                    userDataStore.setUserData({
                        ...userDataStore.userData,
                        lastUsedColours,
                    });
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, { lastUsedColours });
                    // keep local store in sync
                    userDataStore.setUserData({ ...userDataStore.userData, lastUsedColours });
                }
            } catch (err) {
                console.warn('Failed to sync lastUsedColours to user record', err);
            }

            // Track whiteboard save
            mixpanel.track('WhiteboardSaved', {
                email: userDataStore?.userData?.emailAddress,
                handoutId: handoutData?.contentId,
                handoutTitle: handoutData?.contentName,
                saveLocation: saveToMyLibrary ? 'My Library' : 'My Clinic',
            });

            showSnackbar('Whiteboard successfully saved!', 'success');

            if (onSaved) onSaved();
            else onClose();
        } catch (error) {
            console.error('Error saving whiteboard:', error);
            if (!isAuthExpiredError(error) && !isAuthExpiredDialogVisible()) {
                showSnackbar('Failed to save whiteboard. Please try again.', 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    const isBlocked = isAuthExpiredDialogVisible();

    const radioLabelSx = {
        '& .MuiFormControlLabel-label': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 14, sm: 18 },
            lineHeight: { xs: '14px', sm: '18px' },
            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
            color: designTokens.colors.textSecondary,
        },
    };

    return (
        <Dialog
            open={open && !isBlocked}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '30px !important',
                    padding: { xs: '30px 20px', sm: '30px 28px' },
                    width: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxHeight: 'calc(100dvh - 32px)',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                },
            }}
            maxWidth={false}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: { xs: '8px', sm: '24px' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: 26, sm: 32 },
                            lineHeight: { xs: '26px', sm: '32px' },
                            letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                            color: designTokens.colors.textPrimary,
                            pr: '44px',
                        }}
                    >
                        Save customised handout
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            p: 0,
                            width: { xs: '28px', sm: '36px' },
                            height: { xs: '28px', sm: '36px' },
                            background: 'rgb(246, 246, 246)',
                            borderRadius: { xs: '11.928px', sm: '50%' },
                            flexShrink: 0,
                        }}
                    >
                        <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                    </IconButton>
                </Box>

                {saving && <BlockingSpinner open={true} />}

                {!isStudent && (
                    <>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: { xs: 14, sm: 18 },
                                    lineHeight: { xs: '14px', sm: '18px' },
                                    letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                    color: designTokens.colors.textSecondary,
                                    mb: { xs: '12px', sm: '16px' },
                                }}
                            >
                                Please select where you&apos;d like to save your handout.
                            </Typography>
                            <RadioGroup
                                row
                                value={saveToMyLibrary ? 'library' : 'clinic'}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSaveToMyLibrary(val === 'library');
                                }}
                            >
                                <FormControlLabel
                                    value="library"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Save to My handouts"
                                    disabled={!hasUnsavedChanges && isMyHandoutsContext}
                                    sx={radioLabelSx}
                                />
                                <FormControlLabel
                                    value="clinic"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Save to a patient record"
                                    sx={radioLabelSx}
                                />
                            </RadioGroup>
                            {!hasUnsavedChanges && isMyHandoutsContext && (
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: { xs: 13, sm: 14 },
                                        color: designTokens.colors.textSecondary,
                                        mt: 0.5,
                                    }}
                                >
                                    *This customised handout is already saved to My handouts. You can still save it to a
                                    patient record.
                                </Typography>
                            )}
                        </Box>
                        {!isStudent && !saveToMyLibrary && (
                            <>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <RadioGroup
                                        row
                                        value={patientType}
                                        onChange={(e) => {
                                            setPatientType(e.target.value);
                                            if (e.target.value === 'new') {
                                                setSelectedPatient('');
                                                setClinicPatientId('');
                                                setPatientSurname('');
                                                setPatientEmail('');
                                            } else {
                                                setManualPatientName('');
                                                setPatientSurname('');
                                                setPatientEmail('');
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="existing"
                                            control={
                                                <Radio
                                                    sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }}
                                                />
                                            }
                                            label="Existing Patient"
                                            sx={radioLabelSx}
                                        />
                                        <FormControlLabel
                                            value="new"
                                            control={
                                                <Radio
                                                    sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }}
                                                />
                                            }
                                            label="New Patient"
                                            sx={radioLabelSx}
                                        />
                                    </RadioGroup>
                                </Box>

                                {patientType === 'existing' && (
                                    <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                            fullWidth
                                            options={patientList}
                                            loading={patientListLoading}
                                            loadingText="Loading patients..."
                                            getOptionDisabled={(option) =>
                                                !hasUnsavedChanges &&
                                                isPatientSavedContext &&
                                                option.patientId === sourcePatientId
                                            }
                                            getOptionLabel={(option) =>
                                                formatPatientAutocompleteLabel(
                                                    typeof option === 'string' ? { patientName: option } : option,
                                                )
                                            }
                                            value={patientList.find((p) => p.patientId === selectedPatient) || null}
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setSelectedPatient(newValue.patientId);
                                                    setClinicPatientId(newValue.clinicPatientId || '');
                                                    setPatientSurname(newValue.patientSurname || '');
                                                    setPatientEmail(newValue.patientEmail || '');
                                                } else {
                                                    setSelectedPatient('');
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select patient"
                                                    margin="normal"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {patientListLoading && (
                                                                    <CircularProgress color="inherit" size={18} />
                                                                )}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            noOptionsText={patientAutocompleteNoOptionsText}
                                            renderOption={renderPatientAutocompleteOption}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter((patient) => {
                                                    const query = inputValue.toLowerCase();
                                                    return formatPatientAutocompleteSearchText(patient).includes(query);
                                                })
                                            }
                                            sx={patientAutocompleteSx}
                                        />
                                    </FormControl>
                                )}

                                {patientType === 'new' && (
                                    <>
                                        <TextField
                                            label={PATIENT_NAME_LABEL}
                                            fullWidth
                                            margin="normal"
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            required
                                        />
                                        <TextField
                                            label={CLIENT_NAME_LABEL}
                                            fullWidth
                                            margin="normal"
                                            value={patientSurname}
                                            onChange={(e) => setPatientSurname(e.target.value)}
                                            required
                                        />
                                    </>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeAdditionalInfo}
                                            onChange={(e) => setIncludeAdditionalInfo(e.target.checked)}
                                        />
                                    }
                                    label={
                                        patientType === 'existing'
                                            ? 'Show patient details'
                                            : 'Add additional patient details'
                                    }
                                    sx={radioLabelSx}
                                />
                                {includeAdditionalInfo && (
                                    <>
                                        <TextField
                                            label="Patient Email"
                                            fullWidth
                                            margin="normal"
                                            value={patientEmail}
                                            onChange={(e) => setPatientEmail(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />

                                        <TextField
                                            label="Clinic Patient ID"
                                            fullWidth
                                            margin="normal"
                                            value={clinicPatientId}
                                            onChange={(e) => setClinicPatientId(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                    <Button
                        onClick={handleSave}
                        disabled={saving || (!hasUnsavedChanges && !sourceCustomisedHandout?.customisedId)}
                        sx={{
                            backgroundColor: '#FF669B',
                            color: '#FFFFFF',
                            borderRadius: { xs: '100px', sm: 36 },
                            width: { xs: '100%', sm: '160px' },
                            height: { xs: '35px', sm: '44px' },
                            fontSize: { xs: 14, sm: 16 },
                            fontWeight: 500,
                            lineHeight: { xs: '14px', sm: '16px' },
                            letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#e65588' },
                            '&.Mui-disabled': { backgroundColor: 'rgba(255, 102, 155, 0.5)', color: '#FFFFFF' },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SaveWhiteboardModal;
